import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { CsProject } from '../../../../common/models/content-studio.model';

interface ProjectCardProps {
    project: CsProject;
    onDelete?: (uuid: string) => void;
    onDuplicate?: (uuid: string) => void;
    onRename?: (uuid: string, name: string, description: string) => void;
}

/**
 * Project card with a mini canvas preview rendered from canvasGraph.
 * Shows node positions as small colored rectangles — gives a visual
 * fingerprint of the project's workflow at a glance.
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onDelete, onDuplicate, onRename }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editName, setEditName] = useState(project.name);
    const [editDesc, setEditDesc] = useState(project.description || '');

    const updated = project.updatedAt
        ? new Date(project.updatedAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '';

    const nodeCount = project.canvasGraph?.length || 0;
    const hasNodes = nodeCount > 0;

    return (
        <>
        <div
            data-testid="cs-project-card"
            onClick={() => navigate(`/marketing/content-studio/${project.uuid}`)}
            className="group/project cursor-pointer overflow-hidden rounded-xl border bg-card transition-colors hover:border-foreground/25"
        >
            {/* Canvas mini-preview — dotted ground matching the real canvas */}
            <div className="relative aspect-[16/10] overflow-hidden border-b bg-muted bg-[length:8px_8px] bg-[radial-gradient(circle,var(--border)_0.5px,transparent_0.5px)]">
                {hasNodes ? (
                    <CanvasMiniMap nodes={project.canvasGraph!} />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                        {t('CONTENT_STUDIO.EMPTY_CANVAS_TITLE', 'Empty canvas')}
                    </div>
                )}

                {/* Action buttons — visible on hover */}
                <div className="absolute end-2 top-2 z-[2] flex gap-1 opacity-0 transition-opacity group-hover/project:opacity-100">
                    {onRename && (
                        <CardActionBtn
                            onClick={(e) => { e.stopPropagation(); setEditOpen(true); }}
                            tooltip={t('CONTENT_STUDIO.RENAME', 'Rename')}
                        >
                            <Pencil />
                        </CardActionBtn>
                    )}
                    {onDuplicate && (
                        <CardActionBtn
                            onClick={(e) => { e.stopPropagation(); onDuplicate(project.uuid); }}
                            tooltip={t('CONTENT_STUDIO.DUPLICATE', 'Duplicate')}
                        >
                            <Copy />
                        </CardActionBtn>
                    )}
                    {onDelete && (
                        <CardActionBtn
                            onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
                            tooltip={t('CONTENT_STUDIO.DELETE', 'Delete')}
                            destructive
                        >
                            <Trash2 />
                        </CardActionBtn>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-4">
                <p className="truncate font-heading text-sm font-medium tracking-tight text-foreground">
                    {project.name}
                </p>
                {project.description && (
                    <p className="mt-1 truncate text-xs text-muted-foreground">{project.description}</p>
                )}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                        {nodeCount} {t('CONTENT_STUDIO.NODES_COUNT', 'nodes')}
                    </span>
                    <span>{updated}</span>
                </div>
            </div>
        </div>

        {/* Delete confirmation dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t('CONTENT_STUDIO.DELETE_PROJECT_TITLE', 'Delete project?')}</DialogTitle>
                    <DialogDescription>
                        {t(
                            'CONTENT_STUDIO.DELETE_PROJECT_MSG',
                            `"${project.name}" and all its recipes, generations, and assets will be permanently deleted. This cannot be undone.`,
                        )}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setConfirmOpen(false); }}
                    >
                        {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                    </Button>
                    <Button
                        variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation();
                            setConfirmOpen(false);
                            if (onDelete) onDelete(project.uuid);
                        }}
                    >
                        {t('CONTENT_STUDIO.DELETE_PROJECT', 'Delete project')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Rename dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent className="sm:max-w-md" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t('CONTENT_STUDIO.RENAME_PROJECT', 'Rename project')}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="cs-project-name" className="text-muted-foreground">
                            {t('CONTENT_STUDIO.PROJECT_NAME', 'Project name')}
                        </Label>
                        <Input
                            id="cs-project-name"
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="cs-project-description" className="text-muted-foreground">
                            {t('CONTENT_STUDIO.PROJECT_DESCRIPTION', 'Description')}
                        </Label>
                        <Textarea
                            id="cs-project-description"
                            rows={3}
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={(e) => { e.stopPropagation(); setEditOpen(false); }}>
                        {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                    </Button>
                    <Button
                        variant="outline"
                        disabled={!editName.trim()}
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditOpen(false);
                            if (onRename && editName.trim()) onRename(project.uuid, editName.trim(), editDesc.trim());
                        }}
                    >
                        {t('CONTENT_STUDIO.SAVE', 'Save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Card action button helper
// ═══════════════════════════════════════════════════════════════════════

const CardActionBtn: React.FC<{
    onClick: (e: React.MouseEvent) => void;
    tooltip: string;
    destructive?: boolean;
    children: React.ReactNode;
}> = ({ onClick, tooltip, destructive, children }) => (
    <Button
        variant="outline"
        size="icon-sm"
        onClick={onClick}
        title={tooltip}
        aria-label={tooltip}
        // Sits over an unpredictable preview, so it carries its own ground.
        className={
            destructive
                ? 'bg-background/90 text-muted-foreground backdrop-blur-sm hover:bg-destructive/5 hover:text-destructive'
                : 'bg-background/90 text-muted-foreground backdrop-blur-sm hover:text-foreground'
        }
    >
        {children}
    </Button>
);

// ═══════════════════════════════════════════════════════════════════════
// Canvas Mini-Map — renders canvasGraph nodes as small colored rectangles
// ═══════════════════════════════════════════════════════════════════════

const NODE_COLORS: Record<string, string> = {
    product: '#111827',
    recipe: '#6366F1',
    generation: '#16A34A',
    'wall-picker': '#6366F1',
    'edit-image': '#4A4945',
    design: '#4A4945',
};

const NODE_WIDTHS: Record<string, number> = {
    product: 200,
    recipe: 320,
    generation: 220,
    'wall-picker': 320,
    'edit-image': 320,
    design: 260,
};

// Recipe display names + credit costs for the mini-preview. Kept
// module-local because the preview renders from the persisted
// canvasGraph only — it has no access to the live recipes catalog.
const RECIPE_META: Record<string, { name: string; credits: number }> = {
    'lifestyle-room':     { name: 'Lifestyle Room', credits: 15 },
    'lifestyle-scene':    { name: 'Lifestyle Scene', credits: 15 },
    'lifestyle-floor':    { name: 'Lifestyle Floor', credits: 15 },
    'lifestyle-flatlay':  { name: 'Lifestyle Flat-lay', credits: 15 },
    'product-closeup':    { name: 'Product Photography', credits: 10 },
    'style-variation':    { name: 'Variation', credits: 12 },
    'image-to-video':     { name: 'Image to Video', credits: 40 },
    'from-reference':     { name: 'From Reference', credits: 15 },
    'remove-background':  { name: 'Remove Background', credits: 5 },
};

function recipeMeta(slug?: string): { name: string; credits: number } {
    if (!slug) return { name: 'Recipe', credits: 0 };
    return RECIPE_META[slug] || { name: slug.replace(/-/g, ' '), credits: 0 };
}

interface CanvasMiniMapProps {
    nodes: any[];
}

const CanvasMiniMapInner: React.FC<CanvasMiniMapProps> = ({ nodes }) => {
    if (!nodes || nodes.length === 0) return null;

    const valid = nodes.filter(
        (n) => n && Number.isFinite(n.x) && Number.isFinite(n.y) && n.kind,
    );
    if (valid.length === 0) return null;

    const minX = Math.min(...valid.map((n) => n.x));
    const minY = Math.min(...valid.map((n) => n.y));
    const maxX = Math.max(...valid.map((n) => n.x + (NODE_WIDTHS[n.kind] || 200)));
    const maxY = Math.max(...valid.map((n) => n.y + 200));
    const graphW = Math.max(1, maxX - minX);
    const graphH = Math.max(1, maxY - minY);
    const pad = Math.max(60, graphW * 0.15);

    const nodeMap = new Map(valid.map((n) => [n.id, n]));
    const edges: Array<{ fromX: number; fromY: number; toX: number; toY: number }> = [];
    const pushEdge = (parent: any, child: any) => {
        const w = NODE_WIDTHS[parent.kind] || 200;
        edges.push({
            fromX: parent.x + w,
            fromY: parent.y + 100,
            toX: child.x,
            toY: child.y + 100,
        });
    };
    for (const n of valid) {
        // Recipe → Product (input) and Recipe → Generation/WallPicker (outputs)
        if (n.kind === 'recipe') {
            if (n.productNodeId) {
                const parent = nodeMap.get(n.productNodeId);
                if (parent) pushEdge(parent, n);
            }
            for (const extraId of n.additionalSourceNodeIds || []) {
                const extra = nodeMap.get(extraId);
                if (extra) pushEdge(extra, n);
            }
            for (const childId of n.generationNodeIds || []) {
                const child = nodeMap.get(childId);
                if (child) pushEdge(n, child);
            }
        }
        // WallPicker → Generation (each Continue spawns a sibling)
        if (n.kind === 'wall-picker') {
            for (const childId of n.generationNodeIds || []) {
                const child = nodeMap.get(childId);
                if (child) pushEdge(n, child);
            }
        }
        // Edit-image: source generation → edit-image, and edit-image → its
        // own downstream generations
        if (n.kind === 'edit-image') {
            if (n.sourceNodeId) {
                const parent = nodeMap.get(n.sourceNodeId);
                if (parent) pushEdge(parent, n);
            }
            for (const childId of n.generationNodeIds || []) {
                const child = nodeMap.get(childId);
                if (child) pushEdge(n, child);
            }
        }
        // Design source(s) → Design
        if (n.kind === 'design') {
            if (n.sourceNodeId) {
                const parent = nodeMap.get(n.sourceNodeId);
                if (parent) pushEdge(parent, n);
            }
            for (const extraId of n.additionalSourceNodeIds || []) {
                const extra = nodeMap.get(extraId);
                if (extra) pushEdge(extra, n);
            }
        }
    }

    return (
        <svg
            viewBox={`${minX - pad} ${minY - pad} ${graphW + pad * 2} ${graphH + pad * 2}`}
            preserveAspectRatio="xMidYMid meet"
            // `currentColor` on the edges so they follow the theme; the
            // dashed light grey they replaced was invisible on the white
            // card and gone entirely in dark mode.
            className="absolute inset-0 size-full text-muted-foreground"
        >
            {edges.map((e, i) => {
                const midX = (e.fromX + e.toX) / 2;
                return (
                    <path
                        key={`e-${i}`}
                        d={`M ${e.fromX} ${e.fromY} C ${midX} ${e.fromY}, ${midX} ${e.toY}, ${e.toX} ${e.toY}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={6}
                        strokeLinecap="round"
                        opacity={0.9}
                    />
                );
            })}
            {valid.map((n) => {
                const w = NODE_WIDTHS[n.kind] || 200;
                const h = n.kind === 'recipe' ? 340 : 200;
                const color = NODE_COLORS[n.kind] || '#9CA3AF';

                // Thumbnail for product / generation nodes. Videos skip
                // the thumbnail and get a neutral card with a video
                // icon — preview stays legible + stylistically uniform.
                const isVideoGen = n.kind === 'generation' && n.asset?.kind === 'video';
                const imageUrl =
                    n.kind === 'generation' && !isVideoGen && n.asset?.status === 'ready'
                        ? n.asset.thumbnailUrl || n.asset.url
                    : n.kind === 'product' && n.products?.[0]?.thumbnailPath
                        ? n.products[0].thumbnailPath
                    : null;
                const hasImage = !!imageUrl;

                // Recipe nodes get a full card-style rendering below.
                // Video-generation nodes get the neutral video-icon card.
                // Product + image-generation reuse the existing image
                // / label treatment.
                if (n.kind === 'recipe') {
                    return renderRecipePreview(n, w, h, color);
                }
                if (isVideoGen) {
                    return renderVideoPreview(n, w, h);
                }

                const label =
                    n.kind === 'product' ? (n.products?.[0]?.name || 'Product')
                    : n.kind === 'generation' ? (n.asset?.recipeSlug || '').replace(/-/g, ' ')
                    : '';

                return (
                    <g key={n.id}>
                        {/* Background fill — stronger for non-image nodes */}
                        <rect
                            x={n.x}
                            y={n.y}
                            width={w}
                            height={h}
                            rx={10}
                            fill={hasImage ? '#F5F5F5' : color}
                            opacity={hasImage ? 1 : 0.15}
                        />
                        {/* Border (skip for product nodes — too dark) */}
                        {n.kind !== 'product' && (
                        <rect
                            x={n.x}
                            y={n.y}
                            width={w}
                            height={h}
                            rx={10}
                            fill="none"
                            stroke={color}
                            strokeWidth={3}
                            opacity={0.6}
                        />
                        )}
                        {/* Image if available */}
                        {hasImage && (
                            <>
                                <clipPath id={`clip-${n.id}`}>
                                    <rect x={n.x + 4} y={n.y + 4} width={w - 8} height={h - 8} rx={7} />
                                </clipPath>
                                <image
                                    href={imageUrl!}
                                    x={n.x + 4}
                                    y={n.y + 4}
                                    width={w - 8}
                                    height={h - 8}
                                    preserveAspectRatio="xMidYMid slice"
                                    clipPath={`url(#clip-${n.id})`}
                                />
                            </>
                        )}
                        {/* Type label for non-image nodes */}
                        {!hasImage && (
                            <text
                                x={n.x + w / 2}
                                y={n.y + h / 2 + 5}
                                textAnchor="middle"
                                fill={color}
                                fontSize={Math.min(24, w * 0.1)}
                                fontFamily="Manrope, sans-serif"
                                fontWeight={600}
                                opacity={0.6}
                            >
                                {label.length > 18 ? label.slice(0, 18) + '…' : label}
                            </text>
                        )}
                        {/* Colored dot at top-left (skip for product nodes) */}
                        {n.kind !== 'product' && (
                        <circle
                            cx={n.x + 14}
                            cy={n.y + 14}
                            r={6}
                            fill={color}
                            opacity={0.85}
                        />
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

// Memoize so repeated parent renders (hover, animation frames) don't
// re-run the full SVG build. `canvasGraph` is a stable array on the
// project doc — reference equality is a valid shortcut.
export const CanvasMiniMap = React.memo(
    CanvasMiniMapInner,
    (prev, next) => prev.nodes === next.nodes,
);
CanvasMiniMap.displayName = 'CanvasMiniMap';

/**
 * SVG rendering of a recipe node in the mini preview. Mirrors the real
 * RecipeNode: white card, recipe name + credit sub-line, three faint
 * accordion-style row dividers with ghost labels, and a Generate button
 * footer. Recognizable at a glance without needing the real React node.
 */
function renderRecipePreview(n: any, w: number, h: number, accent: string): React.ReactNode {
    const meta = recipeMeta(n.recipeSlug);
    // Pick three likely input labels by slug to hint at what's inside.
    const rowLabels = pickRecipeRowLabels(n.recipeSlug);
    const rowsTop = n.y + 88;
    const rowH = 44;
    const footerY = n.y + h - 68;

    return (
        <g key={n.id}>
            {/* Card background + border */}
            <rect
                x={n.x}
                y={n.y}
                width={w}
                height={h}
                rx={14}
                fill="#FFFFFF"
                stroke="#E5E7EB"
                strokeWidth={3}
            />
            {/* Header: recipe name */}
            <text
                x={n.x + 22}
                y={n.y + 42}
                fill="#111827"
                fontSize={26}
                fontFamily="Manrope, sans-serif"
                fontWeight={600}
            >
                {meta.name}
            </text>
            {/* Credit sub-line */}
            <text
                x={n.x + 22}
                y={n.y + 68}
                fill="#9CA3AF"
                fontSize={18}
                fontFamily="Manrope, sans-serif"
                fontWeight={500}
            >
                {meta.credits} credits
            </text>
            {/* Close "×" hint at top-right */}
            <text
                x={n.x + w - 26}
                y={n.y + 38}
                fill="#D1D5DB"
                fontSize={22}
                fontFamily="Manrope, sans-serif"
            >
                ×
            </text>

            {/* Three accordion rows */}
            {rowLabels.map((label, i) => (
                <g key={`row-${i}`}>
                    <line
                        x1={n.x + 12}
                        x2={n.x + w - 12}
                        y1={rowsTop + i * rowH}
                        y2={rowsTop + i * rowH}
                        stroke="#E5E7EB"
                        strokeWidth={2}
                    />
                    <text
                        x={n.x + 22}
                        y={rowsTop + i * rowH + 28}
                        fill="#6B7280"
                        fontSize={16}
                        fontFamily="Manrope, sans-serif"
                        fontWeight={500}
                    >
                        {label}
                    </text>
                    {/* Chevron on the right */}
                    <text
                        x={n.x + w - 26}
                        y={rowsTop + i * rowH + 30}
                        fill="#D1D5DB"
                        fontSize={12}
                        fontFamily="Manrope, sans-serif"
                        textAnchor="end"
                    >
                        ▾
                    </text>
                </g>
            ))}

            {/* Footer: Generate button */}
            <rect
                x={n.x + 16}
                y={footerY}
                width={w - 32}
                height={52}
                rx={10}
                fill="#FAFAFA"
                stroke="#E5E7EB"
                strokeWidth={2}
            />
            {/* Play triangle */}
            <polygon
                points={`
                    ${n.x + w / 2 - 42},${footerY + 18}
                    ${n.x + w / 2 - 42},${footerY + 34}
                    ${n.x + w / 2 - 28},${footerY + 26}
                `}
                fill="#111827"
            />
            <text
                x={n.x + w / 2 - 18}
                y={footerY + 32}
                fill="#111827"
                fontSize={18}
                fontFamily="Manrope, sans-serif"
                fontWeight={600}
            >
                Generate · {meta.credits} cr
            </text>

            {/* Subtle accent dot top-left, matching live node */}
            <circle cx={n.x + 14} cy={n.y + 14} r={5} fill={accent} opacity={0.4} />
        </g>
    );
}

/**
 * SVG rendering of a video generation node — neutral card with the
 * video-camera icon centered. We intentionally skip the video's
 * thumbnail frame (often a solid color or unhelpful first frame) and
 * show a recognizable placeholder instead.
 */
function renderVideoPreview(n: any, w: number, h: number): React.ReactNode {
    // Center the icon and scale it to the card. Icon's original viewBox
    // is 24×24; we render at ~40% of min(w, h) for balance.
    const iconSize = Math.min(w, h) * 0.4;
    const iconX = n.x + (w - iconSize) / 2;
    const iconY = n.y + (h - iconSize) / 2 - 8; // lift slightly for label below
    const s = iconSize / 24;

    return (
        <g key={n.id}>
            {/* Card surface */}
            <rect
                x={n.x}
                y={n.y}
                width={w}
                height={h}
                rx={12}
                fill="#F5F5F5"
                stroke="#E5E7EB"
                strokeWidth={3}
            />
            {/* Video camera icon — strokes only, viewBox 24×24 scaled */}
            <g transform={`translate(${iconX}, ${iconY}) scale(${s})`} fill="none" stroke="#292D32" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.53 20.4201H6.21C3.05 20.4201 2 18.3201 2 16.2101V7.79008C2 4.63008 3.05 3.58008 6.21 3.58008H12.53C15.69 3.58008 16.74 4.63008 16.74 7.79008V16.2101C16.74 19.3701 15.68 20.4201 12.53 20.4201Z" />
                <path d="M19.52 17.0999L16.74 15.1499V8.83989L19.52 6.88989C20.88 5.93989 22 6.51989 22 8.18989V15.8099C22 17.4799 20.88 18.0599 19.52 17.0999Z" />
                <path d="M11.5 11C12.3284 11 13 10.3284 13 9.5C13 8.67157 12.3284 8 11.5 8C10.6716 8 10 8.67157 10 9.5C10 10.3284 10.6716 11 11.5 11Z" />
            </g>
            {/* "Video" label below the icon */}
            <text
                x={n.x + w / 2}
                y={n.y + h / 2 + iconSize * 0.55}
                textAnchor="middle"
                fill="#6B7280"
                fontSize={18}
                fontFamily="Manrope, sans-serif"
                fontWeight={500}
            >
                Video
            </text>
        </g>
    );
}

/** Three representative input labels per recipe slug. */
function pickRecipeRowLabels(slug?: string): string[] {
    switch (slug) {
        case 'lifestyle-room':    return ['Room type', 'Style', 'Aspect ratio'];
        case 'lifestyle-scene':   return ['Surface', 'Environment', 'Lighting'];
        case 'lifestyle-floor':   return ['Surface', 'Environment', 'Lighting'];
        case 'lifestyle-flatlay': return ['Surface', 'Theme', 'Lighting'];
        case 'product-closeup':   return ['Mode', 'Framing', 'Backdrop'];
        case 'style-variation':   return ['Mode', 'Strength', 'Aspect ratio'];
        case 'image-to-video':    return ['Camera move', 'Intensity', 'Duration'];
        case 'from-reference':    return ['Reference', 'Match strength', 'Aspect ratio'];
        case 'remove-background': return ['Edge refinement', 'Aspect ratio', 'Resolution'];
        default:                  return ['Option', 'Option', 'Option'];
    }
}
